// Lista de todos os minicursos (ativos e inativos) com etapas, alunos em andamento e avisos.
import React, { useMemo, useState } from 'react';
import { FiAlertTriangle, FiChevronRight, FiPlus } from 'react-icons/fi';
import { SeloStatus } from '../relatorios/Graficos';
import { avisosDoCurso, configDoCurso, cursoAtivo, formatarIntervalo, plural } from './util';

const maiuscula = (t) => t.charAt(0).toUpperCase() + t.slice(1);

const FILTROS = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'ativos', rotulo: 'Ativos' },
  { id: 'inativos', rotulo: 'Inativos' },
];

export const seloDoCurso = (curso) => (cursoAtivo(curso)
  ? { rotulo: 'Ativo', tom: 'success' }
  : { rotulo: 'Inativo', tom: 'neutral' });

function ListaCursos({ cursos, etapasPorCurso, andamentoPorCurso, onAbrir, onNovo, onAlternarStatus }) {
  const [filtro, setFiltro] = useState('todos');

  const linhas = useMemo(() => cursos
    .map((c) => {
      const etapas = etapasPorCurso[c.id] || [];
      const avisos = avisosDoCurso(etapas, c).filter((a) => a.nivel !== 'info');
      return { curso: c, etapas: etapas.length, andamento: (andamentoPorCurso[c.id] || []).length, avisos };
    })
    .sort((a, b) => cursoAtivo(b.curso) - cursoAtivo(a.curso)
      || String(a.curso.nome).localeCompare(String(b.curso.nome), 'pt-BR')), [cursos, etapasPorCurso, andamentoPorCurso]);

  const contagem = {
    todos: linhas.length,
    ativos: linhas.filter((l) => cursoAtivo(l.curso)).length,
    inativos: linhas.filter((l) => !cursoAtivo(l.curso)).length,
  };
  const visiveis = linhas.filter((l) => filtro === 'todos' || (filtro === 'ativos') === cursoAtivo(l.curso));

  return (
    <div className="mc-pagina">
      {/* Título no visual original (.minicurso-manager h2, MinicursoManager.css) */}
      <header className="mc-cabecalho mc-cabecalho--original">
        <h2>Gerenciador de Minicursos</h2>
        <p className="mc-suave">Sequências de pílulas e questões que o bot envia pelo WhatsApp, uma etapa por vez.</p>
        <button type="button" className="btn-primary mc-botao" onClick={onNovo}>
          <FiPlus aria-hidden="true" /> Novo Curso
        </button>
      </header>

      {linhas.length === 0 ? (
        <div className="qz-empty mc-vazio">
          <p>Nenhum minicurso criado ainda.</p>
          <button type="button" className="btn-primary mc-botao" onClick={onNovo}>
            <FiPlus aria-hidden="true" /> Criar o primeiro curso
          </button>
        </div>
      ) : (
        <>
          <div className="mc-filtros" role="group" aria-label="Mostrar cursos">
            {FILTROS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`mc-filtro${filtro === f.id ? ' is-ativo' : ''}`}
                aria-pressed={filtro === f.id}
                onClick={() => setFiltro(f.id)}
              >
                {f.rotulo} <span className="mc-filtro__n">{contagem[f.id]}</span>
              </button>
            ))}
          </div>

          <ul className="qz-card mc-cursos">
            {visiveis.map(({ curso, etapas, andamento, avisos }) => {
              const { intervalo } = configDoCurso(curso);
              const graves = avisos.filter((a) => a.nivel === 'perigo').length;
              return (
                <li key={curso.id} className="mc-curso">
                  <div className="mc-curso__principal">
                    <div className="mc-curso__linha">
                      <button type="button" className="mc-curso__nome" onClick={() => onAbrir(curso.id)}>
                        {curso.nome || '(sem nome)'}
                      </button>
                      <SeloStatus status={seloDoCurso(curso)} />
                    </div>
                    <p className="mc-curso__meta">
                      <code className="mc-comando">{curso.comando}</code>
                      <span>{plural(etapas, 'etapa', 'etapas')}</span>
                      <span>
                        {!andamento && 'ninguém em andamento'}
                        {andamento > 0 && (cursoAtivo(curso)
                          ? `${plural(andamento, 'aluno', 'alunos')} em andamento`
                          : `${plural(andamento, 'aluno pausado', 'alunos pausados')} (curso inativo)`)}
                      </span>
                      <span>a cada {formatarIntervalo(intervalo)}</span>
                    </p>
                    {avisos.length > 0 && (
                      <p className={`mc-curso__avisos${graves ? ' is-grave' : ''}`}>
                        <FiAlertTriangle aria-hidden="true" />
                        {maiuscula(avisos.slice(0, 2).map((a) => a.curto).join(' · '))}
                        {avisos.length > 2 ? ` · +${avisos.length - 2}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="mc-curso__acoes">
                    <button
                      type="button"
                      className="btn-secondary mc-botao mc-botao--pequeno"
                      onClick={() => onAlternarStatus(curso)}
                      aria-label={`${cursoAtivo(curso) ? 'Desativar' : 'Ativar'} ${curso.nome}`}
                    >
                      {cursoAtivo(curso) ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost mc-botao mc-botao--pequeno"
                      onClick={() => onAbrir(curso.id)}
                      aria-label={`Editar ${curso.nome}`}
                    >
                      Editar <FiChevronRight aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
            {visiveis.length === 0 && (
              <li className="mc-curso mc-curso--vazio">Nenhum curso {filtro === 'ativos' ? 'ativo' : 'inativo'}.</li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}

export default ListaCursos;
